/**
 * Frota · Manutenção · Central de Manutenção — a visão geral (BI) do módulo.
 *
 * Reproduz a referência `docs/manutencao/central de manutenção.png`: filtros,
 * seis indicadores, alertas prioritários, próximas preventivas, custos dos últimos
 * 6 meses, saúde da frota e linha do tempo recente. Os números vêm de
 * `/maintenance/central`; sem base confiável, o bloco diz que não há dados (não
 * inventa). Não há "vs. período anterior": o plano é um retrato do momento.
 */
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { MaintenanceCentralDto, MaintenancePlanStatusDto } from "@bi/types";
import { api } from "../lib/api";
import { fmtNumber } from "../lib/format";
import { GroupSelect } from "../features/maintenance/GroupSelect";
import { fmtDay } from "../features/maintenance/preventive-ui";
import { IconAlertTriangle, IconBarChart, IconCalendar, IconChevronRight, IconClock, IconFilter, IconPlus, IconTruck, IconDocument } from "../components/icons";
import { Chip, ChipRow, TableOrCards, useMobile } from "../features/maintenance-mobile/mobile-ui";

const Kpi = ({ cor, icone, titulo, valor, nota, to, compacto }: {
  cor: "green" | "red" | "gray" | "amber"; icone: React.ReactNode; titulo: string; valor: number | string; nota: string; to?: string;
  /** celular: sem o ícone grande, título curto em caixa alta — cabem 2 por linha */
  compacto?: boolean;
}) => {
  const topo = { green: "border-t-tm-600", red: "border-t-danger-600", gray: "border-t-gray-300", amber: "border-t-warning-600" }[cor];
  const corpo = compacto ? (
    <div className={`card h-full border-t-4 ${topo} p-3`}>
      <p className="text-[0.6875rem] font-semibold uppercase leading-tight tracking-wide text-gray-500">{titulo}</p>
      <p className="mt-1.5 text-3xl font-bold leading-none tracking-tight text-gray-900">{valor}</p>
      <p className="mt-1 text-[0.6875rem] leading-tight text-gray-500">{nota}</p>
    </div>
  ) : (
    <div className={`card h-full border-t-4 ${topo} p-4 transition-colors ${to ? "hover:bg-surface-muted" : ""}`}>
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-surface-muted text-gray-700">{icone}</span>
        <p className="text-[0.9375rem] font-medium text-gray-800">{titulo}</p>
      </div>
      <p className="mt-3 text-4xl font-bold tracking-tight text-gray-900">{valor}</p>
      <p className="mt-1 text-xs text-gray-500">{nota}</p>
    </div>
  );
  return to ? <Link to={to} className="block">{corpo}</Link> : corpo;
};

const BADGE: Record<MaintenancePlanStatusDto, { t: string; c: string }> = {
  vencido: { t: "Vencida", c: "bg-danger-50 text-danger-700" },
  vencendo: { t: "Atenção", c: "bg-warning-50 text-warning-700" },
  em_dia: { t: "Em dia", c: "bg-tm-50 text-tm-700" },
  sem_base: { t: "Sem base", c: "bg-gray-100 text-gray-600" },
  sem_km: { t: "Sem KM", c: "bg-gray-100 text-gray-600" },
  km_inconsistente: { t: "KM inconsistente", c: "bg-warning-50 text-warning-700" },
};
const Pilula = ({ t, c }: { t: string; c: string }) => <span className={`inline-block rounded-md px-2.5 py-0.5 text-xs font-medium ${c}`}>{t}</span>;

function mesAtual() {
  const d = new Date();
  const ini = new Date(d.getFullYear(), d.getMonth(), 1);
  const fim = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const f = (x: Date) => x.toLocaleDateString("sv-SE");
  return { from: f(ini), to: f(fim) };
}

export function MaintenanceCentralPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const padrao = mesAtual();
  const from = params.get("from") ?? padrao.from;
  const to = params.get("to") ?? padrao.to;
  const plate = params.get("plate") ?? "";
  const group = params.get("group") ?? "";
  const [busca, setBusca] = useState(plate);
  const mobile = useMobile();
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const periodoPadrao = from === padrao.from && to === padrao.to;
  const dm = (iso: string) => iso.slice(8, 10) + "/" + iso.slice(5, 7);

  const setParam = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p, { replace: true });
  };

  const q = useQuery({
    queryKey: ["maintenance", "central", from, to, plate, group],
    queryFn: () => {
      const p = new URLSearchParams({ from: `${from}T00:00:00-03:00`, to: `${to}T23:59:59-03:00` });
      if (plate) p.set("plate", plate);
      if (group) p.set("group", group);
      return api.get<MaintenanceCentralDto>(`/maintenance/central?${p}`);
    },
  });
  const d = q.data;
  const k = d?.kpis;
  const v = (n: number | undefined) => (n == null ? "—" : fmtNumber(n));

  return (
    <div className="space-y-4">
      {mobile ? (
        <>
          <Link to="/frota/manutencao/registrar" className="btn-primary min-h-[3rem] w-full justify-center"><IconPlus className="h-5 w-5" /> Registrar manutenção</Link>
          <ChipRow>
            <Chip on={filtrosAbertos} onClick={() => setFiltrosAbertos((v) => !v)}><IconFilter className="h-4 w-4" /> Filtros</Chip>
            <Chip onClick={() => setFiltrosAbertos(true)}>{periodoPadrao ? "Este mês" : `${dm(from)} – ${dm(to)}`}</Chip>
            {group && <Chip onClick={() => setParam("group", "")}>Grupo filtrado ✕</Chip>}
            {plate && <Chip onClick={() => { setBusca(""); setParam("plate", ""); }}><span className="font-mono">{plate}</span> ✕</Chip>}
          </ChipRow>
        </>
      ) : (
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-4xl font-bold tracking-tight text-gray-900">Central de Manutenção</h2>
            <p className="mt-1 text-lg text-gray-500">Visão geral da manutenção da frota</p>
          </div>
          <Link to="/frota/manutencao/registrar" className="btn-primary min-h-[2.75rem]"><IconPlus className="h-5 w-5" /> Registrar manutenção</Link>
        </header>
      )}

      {(!mobile || filtrosAbertos) && (
      <section className="card grid gap-3 p-4 lg:grid-cols-[1fr_1fr_1fr_auto] lg:items-end">
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-gray-900">Período</span>
          <span className="flex items-center gap-2 rounded-lg border border-surface-border px-3 py-2">
            <IconCalendar className="h-5 w-5 text-gray-500" />
            <input type="date" className="min-w-0 flex-1 bg-transparent text-sm outline-none" value={from} onChange={(e) => setParam("from", e.target.value)} />
            <span className="text-gray-400">–</span>
            <input type="date" className="min-w-0 flex-1 bg-transparent text-sm outline-none" value={to} onChange={(e) => setParam("to", e.target.value)} />
          </span>
        </label>
        <form className="block" onSubmit={(e) => { e.preventDefault(); setParam("plate", busca.trim().toUpperCase().replace(/[^A-Z0-9]/g, "")); }}>
          <span className="mb-1 block text-sm font-semibold text-gray-900">Buscar por placa</span>
          <input
            className="input w-full font-mono uppercase" placeholder="Digite a placa do veículo…" value={busca}
            onChange={(e) => setBusca(e.target.value.toUpperCase())}
            onBlur={() => setParam("plate", busca.trim().replace(/[^A-Z0-9]/g, ""))}
          />
        </form>
        <div>
          <span className="mb-1 block text-sm font-semibold text-gray-900">Grupo de veículos</span>
          <GroupSelect value={group} onChange={(g) => setParam("group", g)} className="w-full" />
        </div>
        <button
          className="btn-secondary min-h-[2.625rem] border-tm-600 text-tm-700"
          onClick={() => { setBusca(""); setParams({}, { replace: true }); }}
        >
          Limpar filtros
        </button>
      </section>
      )}

      <section className={`grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 ${mobile ? "gap-2.5" : "gap-3"}`}>
        <Kpi cor="green" icone={<IconCalendar className="h-6 w-6 text-tm-600" />} titulo="Preventivas próximas" valor={v(k?.preventivesSoon)} nota="itens perto do vencimento" to="/frota/manutencao/preventiva?status=vencendo" compacto={mobile} />
        <Kpi cor="red" icone={<IconAlertTriangle className="h-6 w-6 text-danger-600" />} titulo="Preventivas vencidas" valor={v(k?.preventivesOverdue)} nota="itens vencidos" to="/frota/manutencao/preventiva?status=vencido" compacto={mobile} />
        <Kpi cor="gray" icone={<IconDocument className="h-6 w-6" />} titulo="OS em aberto" valor={v(k?.workOrdersOpen)} nota="ordens ativas" to="/frota/manutencao/ordens?status=active" compacto={mobile} />
        <Kpi cor="red" icone={<IconTruck className="h-6 w-6 text-danger-600" />} titulo="Veículos parados" valor={v(k?.vehiclesStopped)} nota="parados por O.S." to="/frota/manutencao/ordens?status=active" compacto={mobile} />
        <Kpi cor="amber" icone={<IconAlertTriangle className="h-6 w-6 text-warning-600" />} titulo="Itens críticos" valor={v(k?.criticalItems)} nota="ocorrências críticas abertas" to="/frota/manutencao/nao-conformidades" compacto={mobile} />
        <Kpi cor="gray" icone={<IconClock className="h-6 w-6" />} titulo="Sem KM confiável" valor={v(k?.withoutReliableKm)} nota="veículos sem leitura de KM" to="/frota/manutencao/preventiva?status=sem_km" compacto={mobile} />
      </section>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <section className="card overflow-hidden">
          <header className="flex items-center justify-between gap-3 px-4 py-3">
            <h3 className="flex min-w-0 items-center gap-2 text-lg font-semibold leading-tight text-gray-900"><IconAlertTriangle className="h-5 w-5 shrink-0 text-danger-600" /> Alertas prioritários</h3>
            <Link className="shrink-0 whitespace-nowrap text-sm text-info-700 hover:underline" to="/frota/manutencao/preventiva">Ver todos{d ? ` (${d.alertsTotal})` : ""}</Link>
          </header>
          <TableOrCards
            cards={
              <ul className="divide-y divide-surface-border border-t border-surface-border">
                {(d?.alerts ?? []).map((a, i) => (
                  <li key={`${a.plate}-${a.componentCode}-${i}`}>
                    <Link to={`/frota/manutencao/preventiva?plate=${a.plate}`} className="flex min-h-[3.5rem] items-center gap-3 px-4 py-2.5 active:bg-surface-muted">
                      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${a.kind === "vencida" ? "bg-danger-600" : "bg-warning-600"}`} />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-gray-900"><span className="font-mono font-semibold">{a.plate}</span> · {a.amount}</span>
                        <span className="block truncate text-xs text-gray-500">{a.message}</span>
                      </span>
                      <Pilula {...(a.kind === "vencida" ? BADGE.vencido : BADGE.vencendo)} />
                    </Link>
                  </li>
                ))}
                {d && d.alerts.length === 0 && <li className="px-4 py-6 text-center text-sm text-gray-500">Nenhum alerta no momento.</li>}
                {!d && <li className="px-4 py-6 text-center text-sm text-gray-500">Carregando…</li>}
              </ul>
            }
            table={
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-xs uppercase text-gray-500">
              <tr><th className="w-6" /><th className="px-3 py-2 text-left">Placa</th><th className="px-3 py-2 text-left">Mensagem</th><th className="px-3 py-2 text-left">Dias/KM</th><th className="px-3 py-2 text-left">Tipo</th></tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {(d?.alerts ?? []).map((a, i) => (
                <tr key={`${a.plate}-${a.componentCode}-${i}`} className="cursor-pointer hover:bg-surface-muted" onClick={() => navigate(`/frota/manutencao/preventiva?plate=${a.plate}`)}>
                  <td className="pl-4"><span className={`inline-block h-2.5 w-2.5 rounded-full ${a.kind === "vencida" ? "bg-danger-600" : "bg-warning-600"}`} /></td>
                  <td className="px-3 py-3 font-mono text-gray-900">{a.plate}</td>
                  <td className="px-3 py-3 text-gray-800">{a.message}</td>
                  <td className="px-3 py-3 text-gray-800">{a.amount}</td>
                  <td className="px-3 py-3"><Pilula {...(a.kind === "vencida" ? BADGE.vencido : BADGE.vencendo)} /></td>
                </tr>
              ))}
              {d && d.alerts.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">Nenhum alerta no momento.</td></tr>}
              {!d && <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">Carregando…</td></tr>}
            </tbody>
          </table>
            }
          />
        </section>

        <section className="card overflow-hidden">
          <header className="flex items-center justify-between gap-3 px-4 py-3">
            <h3 className="flex min-w-0 items-center gap-2 text-lg font-semibold leading-tight text-gray-900"><IconCalendar className="h-5 w-5 shrink-0 text-tm-600" /> Próximas preventivas</h3>
            <Link className="shrink-0 whitespace-nowrap text-sm text-info-700 hover:underline" to="/frota/manutencao/preventiva">Ver todas{d ? ` (${d.upcomingTotal})` : ""}</Link>
          </header>
          <TableOrCards
            cards={
              <ul className="divide-y divide-surface-border border-t border-surface-border">
                {(d?.upcoming ?? []).map((u, i) => (
                  <li key={`${u.plate}-${u.label}-${i}`}>
                    <Link to={`/frota/manutencao/preventiva?plate=${u.plate}`} className="flex min-h-[3.5rem] items-center gap-3 px-4 py-2.5 active:bg-surface-muted">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-gray-900"><span className="font-mono font-semibold">{u.plate}</span> · {u.label}</span>
                        <span className="block text-xs text-gray-500">
                          {u.nextKm != null ? `próxima em ${fmtNumber(u.nextKm)} km` : `próxima em ${fmtDay(u.nextDate)}`}
                          {u.kmRemaining != null ? ` · faltam ${fmtNumber(u.kmRemaining)} km` : u.daysRemaining != null ? ` · faltam ${u.daysRemaining} dias` : ""}
                        </span>
                      </span>
                      <Pilula {...(u.status === "em_dia" ? BADGE.em_dia : BADGE.vencendo)} />
                      <IconChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
                    </Link>
                  </li>
                ))}
                {d && d.upcoming.length === 0 && <li className="px-4 py-6 text-center text-sm text-gray-500">Nenhuma preventiva com previsão.</li>}
                {!d && <li className="px-4 py-6 text-center text-sm text-gray-500">Carregando…</li>}
              </ul>
            }
            table={
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-muted text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Placa</th><th className="px-3 py-2 text-left">Item</th><th className="px-3 py-2 text-left">Próxima km/data</th>
                  <th className="px-3 py-2 text-left">Atual</th><th className="px-3 py-2 text-left">Faltam</th><th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {(d?.upcoming ?? []).map((u, i) => (
                  <tr key={`${u.plate}-${u.label}-${i}`} className="cursor-pointer hover:bg-surface-muted" onClick={() => navigate(`/frota/manutencao/preventiva?plate=${u.plate}`)}>
                    <td className="px-3 py-3 font-mono text-gray-900">{u.plate}</td>
                    <td className="px-3 py-3 text-gray-800">{u.label}</td>
                    <td className="px-3 py-3 text-gray-800">{u.nextKm != null ? `${fmtNumber(u.nextKm)} km` : fmtDay(u.nextDate)}</td>
                    <td className="px-3 py-3 text-gray-800">{u.currentKm != null ? `${fmtNumber(u.currentKm)} km` : "—"}</td>
                    <td className="px-3 py-3 text-gray-800">
                      {u.kmRemaining != null ? `${fmtNumber(u.kmRemaining)} km` : u.daysRemaining != null ? `${u.daysRemaining} dias` : "—"}
                    </td>
                    <td className="px-3 py-3"><Pilula {...(u.status === "em_dia" ? BADGE.em_dia : BADGE.vencendo)} /></td>
                  </tr>
                ))}
                {d && d.upcoming.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Nenhuma preventiva com previsão.</td></tr>}
                {!d && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Carregando…</td></tr>}
              </tbody>
            </table>
          </div>
            }
          />
        </section>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)]">
        <Custos d={d} />
        <Saude d={d} />
        <Linha d={d} />
      </div>
    </div>
  );
}

/** Rótulo da barra: "R$ 18,6 mil" — o valor cheio não cabe em 6 colunas no celular. */
const fmtBRLCurto = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", notation: "compact", maximumFractionDigits: 1 }).format(v);

function Custos({ d }: { d: MaintenanceCentralDto | undefined }) {
  const meses = d?.costs.months ?? [];
  const max = Math.max(1, ...meses.map((m) => m.cost));
  return (
    <section className="card p-4">
      <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900"><IconBarChart className="h-5 w-5 text-tm-600" /> Custos de manutenção nos últimos 6 meses</h3>
      {d && !d.costs.hasData ? (
        <p className="mt-6 rounded-lg bg-surface-muted p-4 text-sm text-gray-600">
          Ainda não há O.S. do SIAG concluídas com custo lançado nos últimos 6 meses. Os valores aparecem conforme as O.S. são fechadas com serviços e peças.
          {/* As O.S. do ERP (Sinersoft) têm custo e ficam em outras telas;
              sem isso a frase parecia dizer que não houve gasto nenhum. */}
          {" "}O gasto das O.S. do ERP está em{" "}
          <Link to="/frota/manutencao/custos" className="font-medium text-tm-700 hover:underline">Custos de manutenção</Link>.
        </p>
      ) : (
        <div className="mt-4 flex h-52 items-end gap-3">
          {meses.map((m) => (
            <div key={m.month} className="flex h-full flex-1 flex-col items-center justify-end gap-1">
              <span className="text-[0.6875rem] text-gray-600">{m.cost ? fmtBRLCurto(m.cost) : ""}</span>
              <div className="w-full rounded-t-md bg-tm-600" style={{ height: `${Math.max(2, (m.cost / max) * 80)}%` }} title={`${m.workOrders} O.S.`} />
              <span className="text-xs capitalize text-gray-500">{m.label}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Saude({ d }: { d: MaintenanceCentralDto | undefined }) {
  const h = d?.health;
  const total = h?.total ?? 0;
  const fatias = h
    ? [
        { k: "Em dia", n: h.emDia, c: "#16a34a", cls: "bg-tm-600" },
        { k: "Atenção", n: h.atencao, c: "#f59e0b", cls: "bg-warning-600" },
        { k: "Vencida", n: h.vencida, c: "#ef4444", cls: "bg-danger-600" },
        ...(h.semBase ? [{ k: "Sem base", n: h.semBase, c: "#9ca3af", cls: "bg-gray-400" }] : []),
      ]
    : [];
  let acc = 0;
  const R = 70, C = 2 * Math.PI * R;
  return (
    <section className="card p-4">
      <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900"><IconTruck className="h-5 w-5 text-tm-600" /> Saúde da frota</h3>
      {total === 0 ? (
        <p className="mt-6 rounded-lg bg-surface-muted p-4 text-sm text-gray-600">Nenhum veículo com plano preventivo para este filtro.</p>
      ) : (
        <div className="mt-3 flex flex-wrap items-center justify-center gap-6">
          <div className="relative h-44 w-44">
            <svg viewBox="0 0 180 180" className="h-full w-full -rotate-90">
              <circle cx="90" cy="90" r={R} fill="none" stroke="#e5e7eb" strokeWidth="26" />
              {fatias.map((f) => {
                const len = (f.n / total) * C;
                const el = <circle key={f.k} cx="90" cy="90" r={R} fill="none" stroke={f.c} strokeWidth="26" strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-acc} />;
                acc += len;
                return el;
              })}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold text-gray-900">{total}</span>
              <span className="text-sm text-gray-500">veículos</span>
            </div>
          </div>
          <ul className="space-y-2 text-sm">
            {fatias.map((f) => (
              <li key={f.k} className="flex items-center gap-2">
                <span className={`h-3 w-3 rounded-full ${f.cls}`} />
                <span className="w-16 text-gray-800">{f.k}</span>
                <span className="font-semibold text-gray-900">{f.n}</span>
                <span className="text-gray-500">({((f.n / total) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%)</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function Linha({ d }: { d: MaintenanceCentralDto | undefined }) {
  return (
    <section className="card p-4">
      <header className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900"><IconClock className="h-5 w-5 text-tm-600" /> Linha do tempo recente</h3>
        <Link className="shrink-0 whitespace-nowrap text-sm text-info-700 hover:underline" to="/frota/manutencao/ordens?status=completed">Ver todas</Link>
      </header>
      <ul className="mt-3 space-y-3">
        {(d?.timeline ?? []).map((t) => (
          <li key={t.id}>
            <Link to={`/frota/manutencao/ordens/${t.id}`} className="flex items-start gap-3 rounded-lg p-1 hover:bg-surface-muted">
              <span className={`mt-1.5 h-3 w-3 shrink-0 rounded-full ${t.type === "preventive" ? "bg-tm-600" : "bg-danger-600"}`} />
              <span className="w-[5.75rem] shrink-0 text-xs text-gray-500">
                {new Date(t.closedAt).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" })}
              </span>
              <span className="min-w-0">
                <span className="block font-mono text-sm font-semibold text-gray-900">{t.plate}</span>
                <span className="block truncate text-sm text-gray-700">{t.description}</span>
                <span className="block text-xs text-gray-500">O.S. #{t.number} concluída</span>
              </span>
            </Link>
          </li>
        ))}
        {d && d.timeline.length === 0 && <li className="rounded-lg bg-surface-muted p-4 text-sm text-gray-600">Nenhuma O.S. concluída no período.</li>}
      </ul>
    </section>
  );
}
